import { Icon } from "@iconify/react";
import { useRef, useState } from "react";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import { Button } from "../../components";
import { logoTagline, logoText, MAX_OTP_LENGTH, OTP_RESEND_INTERVAL } from "../../constants";
import { useAuth } from "../../hooks";
import { countryCodes } from "../../services/country-codes";
import styles from "./DeleteUser.module.css";

const Option = (props) => {
  return (
    <components.Option {...props}>
      <div className="row">
        <Icon icon={props.data.flag} inline />
        <div className="full row">
          {props.data.label}
          <span className="small gray end"> ({props.data.value})</span>
        </div>
      </div>
    </components.Option>
  );
};

const SingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      {props.data.flag && props.data.flag !== "" ? (
        <Icon icon={props.data.flag} inline />
      ) : (
        <span className="small">{props.data.value}</span>
      )}
    </components.SingleValue>
  );
};

const UserOption = (props) => {
  return (
    <components.Option {...props}>
      <div className="row start">
        <Icon icon={props.data.icon} inline />
        <span className="label">{props.data.label}</span>
        {/* <span className="small gray end"> ({props.data.value})</span> */}
      </div>
    </components.Option>
  );
};

const UserSingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      <div className="row start">
        <Icon icon={props.data.icon} inline />
        <span className="label">{props.data.label}</span>
      </div>
    </components.SingleValue>
  );
};

function DeleteUserPage() {
  const [countryCode, setCountryCode] = useState({
    value: "+91",
    label: "India",
    countryCode: "IN",
    flag: "emojione:flag-for-india",
  }); // Default to India
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState(Array(MAX_OTP_LENGTH).fill("")); // Array to hold OTP digits
  const [isSendOtpDisabled, setIsSendOtpDisabled] = useState(true);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const inputRefs = useRef([]);
  const [confirmTerms, setConfirmTerms] = useState(false);

  const handleMobileNumberChange = (e) => {
    const value = e.target.value;
    setMobileNumber(value);
    setIsSendOtpDisabled(value.length !== 10 || isNaN(value)); // Disable Send OTP button if mobile number is invalid
  };

  const handleResendOtp = () => {
    setOtp(Array(MAX_OTP_LENGTH).fill(""));
    handleSendOtp();
  };

  const { requestOtp, verifyDeleteOtp } = useAuth();
  const handleSendOtp = async () => {
    if (!userType) {
      toast.error("Please select a user type");
      return;
    }

    // const mobile = `${countryCode.value}${mobileNumber}`;
    const { success, error } = await requestOtp(mobileNumber);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success(success);
    setIsOtpSent(true);
    setIsSendOtpDisabled(true);
    setOtpResendTimer(OTP_RESEND_INTERVAL);
    const timer = setInterval(() => {
      setOtpResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < MAX_OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpBackspace = (e, index) => {
    if (e.key === "Backspace" && index > 0 && otp[index] === "") {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== MAX_OTP_LENGTH) {
      toast.error("Please enter a valid OTP.");
      return;
    }
    localStorage.clear();
    const { error, success } = await verifyDeleteOtp(mobileNumber, otpValue, userType.value);
    if (error) {
      // setShowInvalidOTPError(true);
      // setInvalidOTPErrorTimer(5);
      // const timer = setInterval(() => {
      //   setInvalidOTPErrorTimer((prev) => {
      //     if (prev <= 1) {
      //       clearInterval(timer);
      //       setShowInvalidOTPError(false);
      //       return 0;
      //     }
      //     return prev - 1;
      //   });
      // }, 1000);
      return;
    }
    setMobileNumber("");
    setOtp(Array(MAX_OTP_LENGTH).fill(""));
    setIsSendOtpDisabled(true);
    setIsOtpSent(false);
    setOtpResendTimer(0);
    toast.success(success);
    setConfirmTerms(false);
    return;
  };

  const userTypeOptions = [
    { value: "owner", label: "Aurus Owner", icon: "mdi:briefcase-account-outline" },
    { value: "customer", label: "Aurus Group", icon: "mdi:company" },
    { value: "agent", label: "Aurus Partner", icon: "mdi:face-agent" },
  ];
  const [userType, setUserType] = useState(null);

  return (
    <>
      <div className={styles.page}>
        <div className={styles.left}>
          <div className={styles.logo_wrapper}>
            <div className={styles.logo}>{logoText}</div>
            <div className={styles.logo_tagline}>{logoTagline}</div>
          </div>
        </div>
        <div className={styles.right}>
          <form className={styles.form_wrapper}>
            <div className="row">
              <h2 className={styles.title}>Delete User</h2>
              <Select
                options={userTypeOptions}
                components={{ Option: UserOption, SingleValue: UserSingleValue }}
                styles={{
                  menu: (baseStyle) => ({ ...baseStyle, width: "fit-content" }),
                  indicatorSeparator: () => null,
                  control: (baseStyle) => ({
                    ...baseStyle,
                    borderRadius: "0.5rem",
                    border: "1px solid var(--gray-light)",
                    boxShadow: "none",
                    ":hover": {
                      border: "1px solid var(--gray-light)",
                    },
                  }),
                  input: (baseStyle) => ({ ...baseStyle, padding: "0.5rem 0" }),
                }}
                placeholder="Select User"
                value={userType}
                onChange={(e) => setUserType(e)}
                required
              />
            </div>
            <div className={styles.column}>
              <div className={styles.label}>Mobile Number</div>
              <div className={styles.row}>
                <Select
                  options={countryCodes}
                  components={{ Option, SingleValue }}
                  styles={{
                    menu: (baseStyle) => ({ ...baseStyle, width: "fit-content" }),
                    indicatorSeparator: () => null,
                    control: (baseStyle) => ({
                      ...baseStyle,
                      borderRadius: "0.5rem",
                      border: "1px solid var(--gray-light)",
                      boxShadow: "none",
                      backgroundColor: "var(--gray-light-50)",
                      ":hover": {
                        border: "1px solid var(--gray-light)",
                      },
                    }),
                    input: (baseStyle) => ({ ...baseStyle, padding: "0.5rem 0" }),
                  }}
                  value={countryCode}
                  onChange={(e) => setCountryCode(e)}
                />
                <input
                  type="tel"
                  placeholder="Mobile number"
                  className={`full ${styles.input}`}
                  value={mobileNumber}
                  onChange={handleMobileNumberChange}
                  pattern="[0-9]{10}"
                  minLength={10}
                  maxLength={10}
                />
                <Button className="ghost" disabled={isSendOtpDisabled} onClick={handleSendOtp}>
                  Get OTP
                </Button>
              </div>
            </div>
            <div className={styles.column}>
              <div className={styles.label}>Enter OTP</div>
              <div className={styles.row}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    pattern="[0-9]{1}"
                    maxLength={1}
                    className={`${styles.otp_input} ${styles.input}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleOtpBackspace(e, index)}
                    ref={(ref) => (inputRefs.current[index] = ref)}
                    disabled={!isOtpSent}
                  />
                ))}
              </div>
              {isOtpSent ? (
                <div className={styles.row}>
                  <span>Didn't get the OTP?</span>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={otpResendTimer > 0}
                    className={styles.otp_resend}
                  >
                    {otpResendTimer > 0 ? <span className="gray">Resend in {otpResendTimer}s</span> : "Resend OTP"}
                  </button>
                </div>
              ) : (
                ""
              )}
            </div>
            <div className={styles.row}>
              <input
                type="checkbox"
                name="confirm-terms"
                id="confirm-terms"
                onChange={(e) => setConfirmTerms((prev) => !prev)}
                checked={confirmTerms}
                required
                className="checkbox"
              />
              <label htmlFor="confirm-terms" className="label">
                By clicking on the below button, the personal data against this user will be deleted permanently and
                cannot be revoked.
              </label>
            </div>
            <button
              type="submit"
              disabled={!isOtpSent || !confirmTerms}
              className={styles.submit}
              onClick={handleSubmit}
            >
              Delete User
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default DeleteUserPage;
